import { useState } from 'react';
import { CountryData } from 'react-international-phone';
import type { FieldState } from 'final-form';
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js/max';
import { get } from 'lodash';

import { useFormValues } from '@funnels/hooks';
import InternationalMobileTextInputField from '@funnels/ui-components/Fields/TextInputField/InternationalMobileTextInputField';
import { shouldSkipValidate } from '@funnels/utils';

import { TextInputFieldProps } from '../../Fields';

const phoneParser = (country: CountryCode) => (number: string | undefined) => {
  if (!number) {
    return undefined;
  }

  const sanitisedNumber = `${number}`.replace(/[^0-9+]/g, '');
  const result = parsePhoneNumber(sanitisedNumber, country);
  if (result?.isValid()) {
    return result.number.toString();
  }

  return sanitisedNumber;
};

const phoneValidator =
  (
    errorMessage: string,
    isOptional: boolean = false,
    country: CountryCode,
    additionalValidate: (value: any) => any = () => undefined
  ) =>
  (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (shouldSkipValidate(value, isOptional, allValues, fieldMeta)) {
      return;
    }

    try {
      const parsedNumber = parsePhoneNumber(value, country);
      return parsedNumber?.isValid() ? additionalValidate(value) : errorMessage;
    } catch (_e) {
      return errorMessage;
    }
  };

export const InternationalPhoneInput = (props: TextInputFieldProps & { countryList: CountryData[] }) => {
  const {
    disabled,
    label = 'Phone number',
    validationFailMessage = 'Please enter a valid phone number.',
    name,
    isOptional,
    validate: additionalValidate, // only get called after the value passes the default validation
  } = props;

  const handleCountryChange = (value: CountryCode) => {
    const newCountry = value;
    setFormValue(name, undefined);
    setCountry(newCountry);
  };

  // get initial country when loading the form
  const [formValues, setFormValue] = useFormValues();
  const currentPhoneNumber = get(formValues, name);
  const parsedPhoneNumber = parsePhoneNumber(currentPhoneNumber ?? '');
  const initialCountry = parsedPhoneNumber?.country ?? 'AU';

  const [country, setCountry] = useState<CountryCode>(initialCountry);

  return (
    <InternationalMobileTextInputField
      {...props}
      name={name}
      label={label}
      parse={phoneParser(country)}
      onCountryChange={handleCountryChange}
      validate={phoneValidator(validationFailMessage, isOptional, country, additionalValidate)}
      isDisabled={disabled}
    />
  );
};
